import { generatePath } from 'react-router-dom';

import { ALL_SELECTION_ID } from '../utils/selection';

export const URL_ALL_TOKEN = 'all';

export const toUrlGroupId = (groupId?: string) =>
  !groupId || groupId === ALL_SELECTION_ID ? URL_ALL_TOKEN : groupId;

export const toUrlAppId = (appId?: string) =>
  !appId || appId === ALL_SELECTION_ID ? URL_ALL_TOKEN : appId;

export const fromUrlGroupId = (groupId?: string) =>
  groupId === URL_ALL_TOKEN ? ALL_SELECTION_ID : groupId;

export const fromUrlAppId = (appId?: string) =>
  appId === URL_ALL_TOKEN ? ALL_SELECTION_ID : appId;

const GROUP_ROUTE_SEGMENTS = new Set(['create', 'dashboard']);
const APPLICATION_ROUTE_SEGMENTS = new Set(['create', 'dashboard']);

export const buildPath = (
  path: string,
  params?: Record<string, string | number | undefined>
) => generatePath(path, params);

const trimPath = (path: string) => path.replace(/^\/+|\/+$/g, '');

const joinPath = (...segments: Array<string | undefined>) =>
  `/${segments
    .filter(
      (segment): segment is string => segment !== undefined && segment !== ''
    )
    .map((segment) => trimPath(segment))
    .filter(Boolean)
    .join('/')}`;

export const getAppContextFromPath = (pathname: string) => {
  const segments = pathname.split('/').filter(Boolean);
  const groupIndex = segments.indexOf('g');
  if (groupIndex === -1) {
    return { groupId: undefined, appId: undefined };
  }

  const groupId = segments[groupIndex + 1];
  if (!groupId) {
    return { groupId: undefined, appId: undefined };
  }

  if (GROUP_ROUTE_SEGMENTS.has(groupId)) {
    return { groupId: ALL_SELECTION_ID, appId: ALL_SELECTION_ID };
  }

  const sectionSegment = segments[groupIndex + 2];

  if (!sectionSegment) {
    return { groupId: fromUrlGroupId(groupId), appId: ALL_SELECTION_ID };
  }

  if (sectionSegment === 'dashboard') {
    return { groupId: fromUrlGroupId(groupId), appId: ALL_SELECTION_ID };
  }

  const isApplicationsSegment = sectionSegment === 'a';
  const appSegment = isApplicationsSegment
    ? segments[groupIndex + 3]
    : undefined;
  const appId =
    isApplicationsSegment && !APPLICATION_ROUTE_SEGMENTS.has(appSegment ?? '')
      ? appSegment
      : undefined;

  return {
    groupId: fromUrlGroupId(groupId),
    appId: appId ? fromUrlAppId(appId) : undefined,
  };
};

/**
 * App-scoped section segment for a path like /g/:groupId/a/:appId/:section[/...],
 * or undefined when the path is not a concrete application-scoped section (app
 * root, app dashboard, group-scoped or neutral paths). Deeper child segments
 * (e.g. /meters/:id) are dropped so callers land on the section's list root.
 */
export const getApplicationSectionFromPath = (pathname: string) => {
  const segments = pathname.split('/').filter(Boolean);

  if (segments[0] !== 'g' || segments[2] !== 'a') {
    return undefined;
  }

  const appSegment = segments[3];
  if (!appSegment || APPLICATION_ROUTE_SEGMENTS.has(appSegment)) {
    return undefined;
  }

  const section = segments[4];
  if (!section || section === 'dashboard') {
    return undefined;
  }

  return section;
};

/**
 * Group-scoped section segment for a path like /g/:groupId/:section[/...], or
 * undefined when the path is not a concrete group-scoped section (group root,
 * group dashboard/create, an application-scoped path, or a neutral path). Deeper
 * child segments (e.g. /users/:userId) are dropped so callers land on the
 * section's list root.
 */
export const getGroupSectionFromPath = (pathname: string) => {
  const segments = pathname.split('/').filter(Boolean);

  if (segments[0] !== 'g') {
    return undefined;
  }

  const section = segments[2];
  if (!section || section === 'dashboard' || section === 'create') {
    return undefined;
  }

  if (section === 'a') {
    const appSegment = segments[3];
    // /g/:groupId/a or /g/:groupId/a/create is the applications list (group scope).
    if (!appSegment || APPLICATION_ROUTE_SEGMENTS.has(appSegment)) {
      return 'a';
    }
    // /g/:groupId/a/:appId/... is application-scoped, not a plain group section.
    return undefined;
  }

  return section;
};

export const isApplicationGroupDetailPath = (pathname: string) => {
  const segments = pathname.split('/').filter(Boolean);

  return segments.length === 2 && segments[0] === 'g';
};

export const isApplicationsPath = (pathname: string) => {
  const segments = pathname.split('/').filter(Boolean);

  if (segments[0] !== 'g' || segments[2] !== 'a') {
    return false;
  }

  const applicationSegment = segments[3];
  const childSegment = segments[4];

  return (
    !applicationSegment ||
    APPLICATION_ROUTE_SEGMENTS.has(applicationSegment) ||
    !childSegment ||
    childSegment === 'dashboard'
  );
};

export const isUsersPath = (pathname: string) => {
  const segments = pathname.split('/').filter(Boolean);

  return segments.length >= 3 && segments[0] === 'g' && segments[2] === 'users';
};

export const isApiKeysPath = (pathname: string) => {
  const segments = pathname.split('/').filter(Boolean);

  return (
    segments.length >= 3 && segments[0] === 'g' && segments[2] === 'api-keys'
  );
};

export const isScopedAdminPath = (pathname: string) =>
  pathname.split('/').filter(Boolean)[0] === 'g';

export const isScopedDashboardPath = (pathname: string) => {
  const segments = pathname.split('/').filter(Boolean);

  return segments[0] === 'g' && segments[segments.length - 1] === 'dashboard';
};

export const buildApplicationGroupPath = (groupId: string) =>
  buildPath('/g/:groupId', {
    groupId: toUrlGroupId(groupId),
  });

export const buildApplicationGroupsPath = () => buildPath('/g');

export const buildApplicationGroupCreatePath = () => buildPath('/g/create');

export const buildApplicationGroupDashboardPath = (groupId: string) =>
  buildPath('/g/:groupId/dashboard', {
    groupId: toUrlGroupId(groupId),
  });

export const buildApplicationPath = (groupId: string, appId: string) =>
  buildPath('/g/:groupId/a/:appId', {
    groupId: toUrlGroupId(groupId),
    appId: toUrlAppId(appId),
  });

export const buildApplicationDashboardPath = (groupId: string, appId: string) =>
  buildPath('/g/:groupId/a/:appId/dashboard', {
    groupId: toUrlGroupId(groupId),
    appId: toUrlAppId(appId),
  });

export const buildApplicationsPath = (groupId: string) =>
  buildPath('/g/:groupId/a', {
    groupId: toUrlGroupId(groupId),
  });

export const buildUserPath = (groupId: string, id: string) =>
  buildPath('/g/:groupId/users/:id', {
    groupId: toUrlGroupId(groupId),
    id,
  });

export const buildUsersPath = (groupId: string) =>
  buildPath('/g/:groupId/users', {
    groupId: toUrlGroupId(groupId),
  });

export const buildApiKeysPath = (groupId: string) =>
  buildPath('/g/:groupId/api-keys', {
    groupId: toUrlGroupId(groupId),
  });

export const buildGroupScopedPath = (
  groupId: string,
  section: string,
  childPath?: string
) => joinPath('g', toUrlGroupId(groupId), section, childPath);

export const buildApplicationScopedPath = (
  groupId: string,
  appId: string,
  section: string,
  childPath?: string
) =>
  joinPath(
    'g',
    toUrlGroupId(groupId),
    'a',
    toUrlAppId(appId),
    section,
    childPath
  );
